"use client";

import { useTranslations } from "next-intl";
import { useLayoutEffect, useRef } from "react";

import { AppLink } from "@/components/app-link";
import { useCustomerAccount } from "@/providers";

const S21_RISK_SUMMARY_URL =
  "https://universemoney.io/fca-prescribed-risk-summary/";

function setBannerHeight(px: number) {
  document.documentElement.style.setProperty("--s21-banner-height", `${px}px`);
}

export default function S21RiskBanner() {
  const { hasDigitalAssets } = useCustomerAccount();
  const t = useTranslations("s21RiskWarning");
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!hasDigitalAssets || !ref.current) {
      setBannerHeight(0);
      return;
    }

    const el = ref.current;
    setBannerHeight(el.getBoundingClientRect().height);

    const observer = new ResizeObserver(() => {
      setBannerHeight(el.getBoundingClientRect().height);
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      setBannerHeight(0);
    };
  }, [hasDigitalAssets]);

  if (!hasDigitalAssets) return null;

  return (
    <div
      ref={ref}
      className="w-full px-4 py-2 lg:px-6 text-center text-xs font-medium"
      style={{
        backgroundColor:
          "oklch(from var(--s21-risk-color, var(--brand-secondary)) 0.95 calc(c * 0.12) h)",
        color: "var(--s21-risk-color, var(--brand-secondary))",
      }}
    >
      <span>{t("message")}</span>{" "}
      <AppLink
        href={S21_RISK_SUMMARY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2"
        style={{ color: "var(--s21-risk-color, var(--brand-secondary))" }}
      >
        {t("linkText")}
      </AppLink>
    </div>
  );
}
